import type { MutationResult, ReasonInput } from '../../api/contract';
import { createAppError } from '../../api/errors';
import {
  assignmentChangeSetFixture,
  assignmentFixtures,
  directDownlineFixtures,
  memberFixtures,
} from './fixtures';
import type { MemberRepository } from './repository';
import type {
  AssignmentChangeSet,
  AssignmentPreviewInput,
  DirectDownline,
  DownlineQuery,
  MemberAssignment,
  MemberDetail,
  MemberListQuery,
} from './types';

export interface MockMemberRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
  readonly now?: string;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const memberDetailAliases: Readonly<Record<string, string>> = {
  'member-a': memberFixtures[0]!.id,
};
const changeSetDetailAliases: Readonly<Record<string, string>> = {
  'change-a': assignmentChangeSetFixture.id,
};

export class MockMemberRepository implements MemberRepository {
  readonly #mode: 'data' | 'empty' | 'error';
  readonly #nowMilliseconds: number;
  #members: MemberDetail[];
  #changeSets: AssignmentChangeSet[];
  #latestPreview: { readonly fingerprint: string; readonly hash: string } | null = null;

  constructor(options: MockMemberRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
    this.#nowMilliseconds = Date.parse(options.now ?? mockNow);
    this.#members = this.#mode === 'empty' ? [] : memberFixtures.map(cloneMember);
    this.#changeSets = [cloneChangeSet(assignmentChangeSetFixture)];
  }

  async list(query: MemberListQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    const filtered = this.#members
      .filter((member) => matchesMember(member, query))
      .sort((left, right) => compare(left, right, query));
    const start = Math.max(0, query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map(cloneMember),
      total: filtered.length,
      requestId: 'mock-members-list',
    };
  }

  async get(memberId: string, signal: AbortSignal) {
    this.#assertAvailable(signal);
    return cloneMember(this.#find(memberId));
  }

  async listDownlines(memberId: string, query: DownlineQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    this.#find(memberId);
    const filtered = [...(directDownlineFixtures[memberId] ?? [])]
      .filter((item) => matchesDownline(item, query))
      .sort((left, right) => compare(left, right, query));
    const start = Math.max(0, query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map((item) => ({ ...item })),
      total: filtered.length,
      requestId: 'mock-member-downlines',
    };
  }

  async listAssignments(
    memberId: string,
    signal: AbortSignal,
  ): Promise<readonly MemberAssignment[]> {
    this.#assertAvailable(signal);
    this.#find(memberId);
    return (assignmentFixtures[memberId] ?? []).map(cloneAssignment);
  }

  changeStatus(
    memberId: string,
    input: { readonly status: MemberDetail['status']; readonly reason: string },
  ) {
    this.#assertAvailable();
    const current = this.#find(memberId);
    if (current.status === 'CLOSED' && input.status !== 'CLOSED') {
      return Promise.reject(conflict('Closed Members cannot be reopened', 'mock-member-terminal'));
    }
    const updated = { ...current, status: input.status, updatedAt: mockTimestamp };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-member-status'));
  }

  bindPartnerUser(
    memberId: string,
    input: { readonly partnerUserId: string; readonly expectedUsername: string },
  ) {
    this.#assertAvailable();
    const current = this.#find(memberId);
    if (
      current.memberType !== 'HOUSE' ||
      current.username?.toLocaleLowerCase() !== input.expectedUsername.toLocaleLowerCase()
    ) {
      return Promise.reject(conflict('HOUSE identity does not match', 'mock-house-binding'));
    }
    if (current.partnerUserId !== null && current.partnerUserId !== input.partnerUserId) {
      return Promise.reject(conflict('HOUSE identity is immutable', 'mock-house-binding'));
    }
    const updated = { ...current, partnerUserId: input.partnerUserId, updatedAt: mockTimestamp };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-house-binding'));
  }

  rotateReferralCode(memberId: string, _input: ReasonInput) {
    this.#assertAvailable();
    const current = this.#find(memberId);
    if (current.status === 'CLOSED' || current.memberType === 'HOUSE') {
      return Promise.reject(
        conflict('Only active NORMAL Members can rotate referral codes', 'mock-referral-terminal'),
      );
    }
    const updated = {
      ...current,
      activeReferralCodes: ['REF_0ZHJKMNPQRSTVWXY'],
      updatedAt: mockTimestamp,
    };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-member-referral'));
  }

  previewAssignment(input: AssignmentPreviewInput) {
    this.#assertAvailable();
    const effective = input.effectiveAt === undefined ? Number.NaN : Date.parse(input.effectiveAt);
    if (
      input.mode !== 'SCHEDULED' ||
      !Number.isFinite(effective) ||
      effective <= this.#nowMilliseconds
    ) {
      return Promise.reject(
        conflict('Effective time must be in the future', 'mock-assignment-time'),
      );
    }
    for (const change of input.changes) this.#find(change.memberId);
    const fingerprint = JSON.stringify(input);
    const hash = 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
    this.#latestPreview = { fingerprint, hash };
    return Promise.resolve({
      mode: input.mode,
      effectiveAt: input.effectiveAt ?? null,
      changes: input.changes.map((change) => {
        const current = this.#find(change.memberId).currentAssignment;
        return {
          memberId: change.memberId,
          before:
            current === null || current.upline === null
              ? null
              : { agentId: current.agent.id, uplineMemberId: current.upline.memberId },
          after: { agentId: change.agentId, uplineMemberId: change.uplineMemberId },
        };
      }),
      warnings: [],
      blockers: [],
      previewHash: hash,
    });
  }

  createAssignmentChangeSet(input: AssignmentPreviewInput & { readonly previewHash: string }) {
    this.#assertAvailable();
    const { previewHash: submittedHash, ...previewInput } = input;
    if (
      this.#latestPreview === null ||
      this.#latestPreview.hash !== submittedHash ||
      this.#latestPreview.fingerprint !== JSON.stringify(previewInput)
    ) {
      return Promise.reject(conflict('A fresh matching preview is required', 'mock-preview-stale'));
    }
    const created: AssignmentChangeSet = {
      ...cloneChangeSet(assignmentChangeSetFixture),
      effectiveAt: input.effectiveAt ?? assignmentChangeSetFixture.effectiveAt,
      reason: input.reason,
      previewHash: submittedHash,
      changes: input.changes.map((change, index) => ({
        id: `00000000-0000-4000-8000-${String(900 + index).padStart(12, '0')}`,
        memberId: change.memberId,
        before: null,
        after: { agentId: change.agentId, uplineMemberId: change.uplineMemberId },
        predecessorAssignmentId: null,
        successorAssignmentId: null,
      })),
    };
    this.#changeSets = [created, ...this.#changeSets.filter((item) => item.id !== created.id)];
    this.#latestPreview = null;
    return Promise.resolve(result(created, 'mock-assignment-create'));
  }

  async getAssignmentChangeSet(changeSetId: string, signal: AbortSignal) {
    this.#assertAvailable(signal);
    return cloneChangeSet(this.#findChangeSet(changeSetId));
  }

  cancelAssignmentChangeSet(changeSetId: string, input: ReasonInput) {
    this.#assertAvailable();
    const current = this.#findChangeSet(changeSetId);
    const effectiveAt = Date.parse(current.effectiveAt);
    if (
      current.status !== 'SCHEDULED' ||
      !Number.isFinite(effectiveAt) ||
      effectiveAt <= this.#nowMilliseconds
    ) {
      return Promise.reject(
        conflict('Only scheduled change sets can be cancelled', 'mock-cancel-terminal'),
      );
    }
    const cancelled = {
      ...current,
      status: 'CANCELLED' as const,
      cancelledAt: mockTimestamp,
      cancelledBy: mockActor,
      cancellationReason: input.reason,
    };
    this.#changeSets = this.#changeSets.map((item) => (item.id === changeSetId ? cancelled : item));
    return Promise.resolve(result(cancelled, 'mock-assignment-cancel'));
  }

  #assertAvailable(signal?: AbortSignal) {
    if (signal?.aborted === true) throw new DOMException('Member request aborted', 'AbortError');
    if (this.#mode === 'error') {
      throw createAppError({
        status: 503,
        code: 'MOCK_MEMBERS_UNAVAILABLE',
        message: 'Member fixture is unavailable',
        requestId: 'mock-members-error',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
  }

  #find(memberId: string) {
    const resolvedId = memberDetailAliases[memberId] ?? memberId;
    const member = this.#members.find((item) => item.id === resolvedId);
    if (member === undefined) throw notFound('Member not found', 'mock-member-not-found');
    return member;
  }

  #findChangeSet(changeSetId: string) {
    const resolvedChangeSetId = changeSetDetailAliases[changeSetId] ?? changeSetId;
    const changeSet = this.#changeSets.find((item) => item.id === resolvedChangeSetId);
    if (changeSet === undefined)
      throw notFound('Assignment change set not found', 'mock-change-set-not-found');
    return changeSet;
  }

  #replace(member: MemberDetail) {
    this.#members = this.#members.map((item) => (item.id === member.id ? member : item));
  }
}

function matchesMember(member: MemberDetail, query: MemberListQuery) {
  const assignment = member.currentAssignment;
  return (
    includes(member.partnerId, query.filters.partnerId) &&
    includes(assignment?.agent.id ?? '', query.filters.agentId) &&
    includes(assignment?.upline?.memberId ?? '', query.filters.uplineMemberId) &&
    (query.filters.memberType === '' || member.memberType === query.filters.memberType) &&
    (query.filters.status === '' || member.status === query.filters.status) &&
    includes(member.username ?? '', query.filters.username) &&
    includes(member.partnerUserId ?? '', query.filters.partnerUserId) &&
    member.activeReferralCodes.some((code) => includes(code, query.filters.referralCode))
  );
}

function matchesDownline(item: DirectDownline, query: DownlineQuery) {
  return (
    (query.filters.status === '' || item.status === query.filters.status) &&
    includes(item.username ?? '', query.filters.username) &&
    includes(item.partnerUserId ?? '', query.filters.partnerUserId)
  );
}

function compare<T extends object>(
  left: T,
  right: T,
  query: { readonly sort: string; readonly order: 'asc' | 'desc' },
) {
  const leftValue = String((left as Record<string, unknown>)[query.sort] ?? '');
  const rightValue = String((right as Record<string, unknown>)[query.sort] ?? '');
  const order = leftValue.localeCompare(rightValue);
  return query.order === 'asc' ? order : -order;
}

function includes(value: string, search: string) {
  return value.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
}

function cloneMember(member: MemberDetail): MemberDetail {
  return {
    ...member,
    activeReferralCodes: [...member.activeReferralCodes],
    currentAssignment:
      member.currentAssignment === null
        ? null
        : {
            ...member.currentAssignment,
            agent: { ...member.currentAssignment.agent },
            upline:
              member.currentAssignment.upline === null
                ? null
                : { ...member.currentAssignment.upline },
          },
    defaultWallet: member.defaultWallet === null ? null : { ...member.defaultWallet },
  };
}

function cloneAssignment(item: MemberAssignment): MemberAssignment {
  return {
    ...item,
    agent: { ...item.agent },
    upline: item.upline === null ? null : { ...item.upline },
  };
}

function cloneChangeSet(item: AssignmentChangeSet): AssignmentChangeSet {
  return {
    ...item,
    changes: item.changes.map((change) => ({
      ...change,
      before: change.before === null ? null : { ...change.before },
      after: { ...change.after },
    })),
  };
}

function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data, requestId };
}

function conflict(message: string, requestId: string) {
  return createAppError({
    status: 409,
    code: 'MEMBER_CONFLICT',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function notFound(message: string, requestId: string) {
  return createAppError({
    status: 404,
    code: 'MEMBER_NOT_FOUND',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

const mockNow = '2026-07-23T00:00:00+07:00';
const mockTimestamp = '2026-07-23T12:00:00+07:00';
const mockActor = '00000000-0000-4000-8000-000000000001';
